import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";

const CATEGORIES = ["Maquillage", "Lèvres", "Parfums", "Sacs & Accessoires", "Soins", "Autre"];

export default function Idees() {
  const [form, setForm] = useState({ pseudo: "", category: "Maquillage", brand: "", product_name: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/ideas", form);
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10" data-testid="idees-page">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-6">Tes idées</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="idees-title">
            <MaskedLine delay={0.1}>Qu'aimerais-tu</MaskedLine>
            <MaskedLine delay={0.25}>voir dans notre</MaskedLine>
            <MaskedLine delay={0.4} className="italic text-kay-pink">boutique ?</MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 text-kay-ink/60 text-base md:text-lg leading-relaxed max-w-md" data-testid="idees-subtitle">
              Une marque, un produit ou une nouveauté que tu rêves de retrouver à petit prix ? Envoie-nous ton idée !
            </p>
            <div className="mt-10 border-t border-kay-line pt-8 flex gap-4 items-start">
              <Lightbulb className="text-kay-pink shrink-0 mt-1" size={20} />
              <p className="text-sm text-kay-ink/50 leading-relaxed">
                Les idées les plus demandées remontent en tête de notre liste d'achats. Ton idée peut vraiment
                devenir la prochaine pépite de la boutique.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.3} className="lg:col-span-7">
          {sent ? (
            <div className="rounded-3xl border border-kay-line bg-white p-10 lg:p-14 text-center shadow-[0_10px_30px_rgba(232,160,191,0.12)]" data-testid="idees-success">
              <span className="inline-flex w-16 h-16 rounded-full bg-kay-rose items-center justify-center mb-6">
                <Send className="text-kay-pink" size={26} />
              </span>
              <h2 className="font-display text-3xl sm:text-4xl">Merci, c'est envoyé !</h2>
              <p className="mt-4 text-kay-ink/60 text-sm max-w-sm mx-auto">
                Ton idée rejoint la wishlist de la boutique. Si elle est souvent demandée, tu la verras bientôt à petit prix.
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <button
                  data-testid="idees-another-button"
                  onClick={() => {
                    setSent(false);
                    setForm({ pseudo: "", category: "Maquillage", brand: "", product_name: "", message: "" });
                  }}
                  className="rounded-full border border-kay-ink px-6 py-3 text-sm font-semibold hover:bg-kay-ink hover:text-white transition-all"
                >
                  Proposer une autre idée
                </button>
                <Link to="/boutique" className="rounded-full bg-kay-ink text-white px-6 py-3 text-sm font-semibold flex items-center gap-2 hover:bg-kay-pink transition-colors">
                  Voir la boutique <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-kay-line bg-white p-7 lg:p-10 shadow-[0_10px_30px_rgba(232,160,191,0.12)] grid gap-5" data-testid="idees-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="grid gap-2">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-kay-ink/50">Prénom ou pseudo (facultatif)</span>
                  <input data-testid="idees-pseudo-input" value={form.pseudo} onChange={set("pseudo")} placeholder="Ex : Lina" className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink transition-colors" />
                </label>
                <label className="grid gap-2">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-kay-ink/50">Catégorie souhaitée</span>
                  <select data-testid="idees-category-select" value={form.category} onChange={set("category")} className="rounded-xl border border-kay-line bg-white px-4 py-3 text-sm outline-none focus:border-kay-pink transition-colors">
                    {CATEGORIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="grid gap-2">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-kay-ink/50">Marque souhaitée</span>
                  <input data-testid="idees-brand-input" value={form.brand} onChange={set("brand")} placeholder="Ex : Fenty Beauty" className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink transition-colors" />
                </label>
                <label className="grid gap-2">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-kay-ink/50">Nom du produit *</span>
                  <input data-testid="idees-product-input" required value={form.product_name} onChange={set("product_name")} placeholder="Ex : Fond de teint Pro Filt'r" className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink transition-colors" />
                </label>
              </div>
              <label className="grid gap-2">
                <span className="font-mono text-[11px] uppercase tracking-widest text-kay-ink/50">Raconte-nous ton idée</span>
                <textarea data-testid="idees-message-textarea" rows={4} value={form.message} onChange={set("message")} placeholder="Pourquoi tu l'adores, où tu l'as vu, le prix que tu as vu ailleurs…" className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink transition-colors resize-none" />
              </label>
              <button
                data-testid="idees-submit-button"
                type="submit"
                disabled={loading}
                className="rounded-full bg-kay-ink text-white py-4 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-kay-pink transition-colors disabled:opacity-50"
              >
                <Send size={15} /> {loading ? "Envoi…" : "Envoyer mon idée"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
